import React, { useState } from 'react';
import {
  Box,
  Alert,
  Button,
  Stack,
  Chip,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  RestoreFromTrash as RestoreIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { recoverFlowExecution } from '../../api/agenticFlow.api';

function InlineErrorRecovery({
  error,
  executionId,
  onRetry,
  onRecoverySuccess,
  onShowDetails,
  compact = false,
}) {
  const [recovering, setRecovering] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const getErrorSeverity = (error) => {
    if (!error) return 'info';
    const errorStr = error.toString().toLowerCase();

    if (errorStr.includes('timeout') || errorStr.includes('network')) return 'warning';
    if (errorStr.includes('validation') || errorStr.includes('serialization')) return 'error';
    if (errorStr.includes('interrupt') || errorStr.includes('form')) return 'info';

    return 'error';
  };

  const getErrorIcon = (severity) => {
    switch (severity) {
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  const getQuickRecoveryText = (error) => {
    if (!error) return 'An error occurred';
    const errorStr = error.toString().toLowerCase();

    if (errorStr.includes('timeout')) return 'Connection timed out';
    if (errorStr.includes('network')) return 'Network connection failed';
    if (errorStr.includes('validation')) return 'Data validation failed';
    if (errorStr.includes('api')) return 'External service unavailable';

    return 'Flow execution failed';
  };

  const handleQuickRecovery = async () => {
    if (!executionId) {
      onRetry?.();
      return;
    }

    setRecovering(true);

    try {
      const result = await recoverFlowExecution(executionId);

      if (result.success) {
        onRecoverySuccess?.(result);
      } else {
        throw new Error(result.error || 'Recovery failed');
      }
    } catch (error) {
      console.error('Quick recovery failed:', error);
      // Fall back to retry
      onRetry?.();
    } finally {
      setRecovering(false);
    }
  };

  const severity = getErrorSeverity(error);
  const quickText = getQuickRecoveryText(error);

  if (compact) {
    return (
      <Box sx={{ my: 1 }}>
        <Alert
          severity={severity}
          action={(
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={recovering ? <CircularProgress size={16} /> : <RestoreIcon />}
                onClick={handleQuickRecovery}
                disabled={recovering}
              >
                {recovering ? 'Fixing...' : 'Quick Fix'}
              </Button>
              {onShowDetails && (
                <Button size="small" onClick={onShowDetails}>
                  Details
                </Button>
              )}
            </Stack>
          )}
        >
          {quickText}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      <Alert
        severity={severity}
        icon={getErrorIcon(severity)}
        action={(
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label="expand"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      >
        <Stack spacing={1}>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            {quickText}
          </Typography>

          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {error}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onRetry}
                  disabled={recovering}
                >
                  Retry
                </Button>

                {executionId && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={recovering ? <CircularProgress size={16} /> : <RestoreIcon />}
                    onClick={handleQuickRecovery}
                    disabled={recovering}
                  >
                    {recovering ? 'Recovering...' : 'Smart Recovery'}
                  </Button>
                )}

                {onShowDetails && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={onShowDetails}
                  >
                    More Options
                  </Button>
                )}
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </Alert>
    </Box>
  );
}

export default InlineErrorRecovery;
